import React, { useState } from 'react';
import { Link } from 'react-router-dom';
interface ContactFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
}
const emptyContact: ContactFormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  message: ''
};
export function Contact() {
  const [form, setForm] = useState<ContactFormState>(emptyContact);
  const [smsAutomated, setSmsAutomated] = useState(false);
  const [smsPromotional, setSmsPromotional] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormState, string>>>({});
  const [submitted, setSubmitted] = useState(false);
  const setField = (key: keyof ContactFormState, value: string) => {
    setForm(f => ({
      ...f,
      [key]: value
    }));
    setErrors(e => ({
      ...e,
      [key]: undefined
    }));
  };
  const isValidPhone = (value: string) => {
    const digits = value.replace(/\D/g, '');
    return digits.length === 10 || digits.length === 11 && digits.startsWith('1');
  };
  const validate = (): boolean => {
    const next: Partial<Record<keyof ContactFormState, string>> = {};
    if (!form.firstName.trim()) next.firstName = 'Required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Enter a valid email.';
    if (!isValidPhone(form.phone)) next.phone = 'Enter a valid US phone number.';
    if (!form.message.trim()) next.message = 'Required.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitted(true);
  };
  const inputClass = (key: keyof ContactFormState) => `w-full bg-neutral-900 border px-4 py-3 text-white focus:outline-none rounded-sm transition-colors ${errors[key] ? 'border-red-500/70 focus:border-red-500' : 'border-neutral-700 focus:border-vsc-gold'}`;
  return <div className="py-24 px-4" data-id="element-170">
      <div className="max-w-2xl mx-auto w-full" data-id="element-171">
        <div className="text-center mb-10" data-id="element-172">
          <h1 className="font-display text-4xl md:text-5xl mb-4 tracking-wide" data-id="element-173">
            Contact Us
          </h1>
          <p className="text-neutral-400 max-w-xl mx-auto" data-id="element-174">
            Questions about an order, sizing, or anything else? Send us a
            message and opt in to stay in the loop.
          </p>
        </div>

        {submitted ? <div className="bg-neutral-900/50 border border-vsc-gold/40 rounded-sm p-8 text-center" data-id="element-175">
            <p className="font-display text-3xl tracking-wide text-vsc-gold mb-2" data-id="element-176">
              Thanks — message received
            </p>
            <p className="text-neutral-300" data-id="element-177">
              We'll get back to you soon. If you opted in to text messages,
              reply STOP at any time to unsubscribe or HELP for help.
            </p>
          </div> : <form onSubmit={handleSubmit} className="space-y-5" noValidate data-id="element-178">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-179">
              <div data-id="element-180">
                <label className="block text-sm text-neutral-300 mb-1.5" data-id="element-181">First name *</label>
                <input value={form.firstName} onChange={e => setField('firstName', e.target.value)} className={inputClass('firstName')} aria-invalid={!!errors.firstName} data-id="element-182" />
                {errors.firstName && <p className="text-red-400 text-xs mt-1" data-id="element-183">{errors.firstName}</p>}
              </div>
              <div data-id="element-184">
                <label className="block text-sm text-neutral-300 mb-1.5" data-id="element-185">Last name</label>
                <input value={form.lastName} onChange={e => setField('lastName', e.target.value)} className={inputClass('lastName')} data-id="element-186" />
              </div>
            </div>

            <div data-id="element-187">
              <label className="block text-sm text-neutral-300 mb-1.5" data-id="element-188">Email *</label>
              <input type="email" value={form.email} onChange={e => setField('email', e.target.value)} className={inputClass('email')} aria-invalid={!!errors.email} data-id="element-189" />
              {errors.email && <p className="text-red-400 text-xs mt-1" data-id="element-190">{errors.email}</p>}
            </div>

            <div data-id="element-191">
              <label className="block text-sm text-neutral-300 mb-1.5" data-id="element-192">Phone *</label>
              <input type="tel" placeholder="(555) 555-5555" value={form.phone} onChange={e => setField('phone', e.target.value)} className={inputClass('phone')} aria-invalid={!!errors.phone} data-id="element-193" />
              {errors.phone && <p className="text-red-400 text-xs mt-1" data-id="element-194">{errors.phone}</p>}
            </div>

            <div data-id="element-195">
              <label className="block text-sm text-neutral-300 mb-1.5" data-id="element-196">Message *</label>
              <textarea rows={4} value={form.message} onChange={e => setField('message', e.target.value)} className={`${inputClass('message')} resize-y`} aria-invalid={!!errors.message} data-id="element-197" />
              {errors.message && <p className="text-red-400 text-xs mt-1" data-id="element-198">{errors.message}</p>}
            </div>

            {/* Consent — checkboxes unchecked by default, never required to submit */}
            <div className="space-y-4 pt-2" data-id="element-199">
              <label className="flex gap-3 text-left cursor-pointer" data-id="element-200">
                <input type="checkbox" checked={smsAutomated} onChange={e => setSmsAutomated(e.target.checked)} className="mt-1 h-4 w-4 flex-shrink-0 accent-vsc-gold" data-id="element-201" />
                <span className="text-xs text-neutral-300 leading-relaxed" data-id="element-202">
                  By checking this box, you consent to receive{' '}
                  <strong className="text-white" data-id="element-203">automated follow-up and reminder</strong>{' '}
                  SMS messages from VSCCLOTHING d/b/a VSC Clothing with varying frequency.
                  Msg and data rates may apply. Reply STOP to end. Text HELP for help. See{' '}
                  <Link to="/sms-terms" className="underline hover:text-white" data-id="element-204">SMS Terms</Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="underline hover:text-white" data-id="element-205">Privacy Policy</Link>.
                </span>
              </label>

              <label className="flex gap-3 text-left cursor-pointer" data-id="element-206">
                <input type="checkbox" checked={smsPromotional} onChange={e => setSmsPromotional(e.target.checked)} className="mt-1 h-4 w-4 flex-shrink-0 accent-vsc-gold" data-id="element-207" />
                <span className="text-xs text-neutral-300 leading-relaxed" data-id="element-208">
                  By checking this box, you consent to receive{' '}
                  <strong className="text-white" data-id="element-209">promotional</strong>{' '}
                  SMS messages from VSCCLOTHING d/b/a VSC Clothing with varying frequency.
                  Msg and data rates may apply. Reply STOP to end. Text HELP for help. See{' '}
                  <Link to="/sms-terms" className="underline hover:text-white" data-id="element-210">SMS Terms</Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="underline hover:text-white" data-id="element-211">Privacy Policy</Link>.
                </span>
              </label>

              <label className="flex gap-3 text-left cursor-pointer" data-id="element-212">
                <input type="checkbox" checked={newsletter} onChange={e => setNewsletter(e.target.checked)} className="mt-1 h-4 w-4 flex-shrink-0 accent-vsc-gold" data-id="element-213" />
                <span className="text-xs text-neutral-300 leading-relaxed" data-id="element-214">
                  Yes, subscribe me to your newsletter.
                </span>
              </label>
            </div>

            <button type="submit" className="w-full bg-vsc-gold text-black font-medium py-4 text-lg hover:bg-white transition-colors rounded-sm" data-id="element-215">
              Submit
            </button>
          </form>}
      </div>
    </div>;
}